import Typesense from 'typesense';
import map from 'lodash/map';
import compact from 'lodash/compact';
import shuffle from 'lodash/shuffle';
import split from 'lodash/split';
import moment from 'moment';
import config from '../../../config';
import * as types from './constants';
import {
  getActiveShop,
  getBarberUser,
  getBarberUserId,
  getIsRentalShop,
  getShopOrBrandIdAndType,
} from '../../auth/selectors';
import { flagNames, getFeatureFlags } from '../../../modules/infra/feature-flags';

const searchRequest = value => ({
  type: types.SEARCH_REQUEST,
  value,
});

const searchSuccess = results => ({
  type: types.SEARCH_SUCCESS,
  results,
});

const searchError = error => ({
  type: types.SEARCH_ERROR,
  error,
});

const getClient = state => new Typesense.Client({
  nodes: shuffle(config.typesense.url.map(url => ({ url }))),
  apiKey: state.auth.user.scopedSearchKey,
});

const getIndex = (state, index) => getClient(state).collections(index);

const shouldEnableInfix = (value) => {
  const terms = compact(split(value, config.typesense.delimiterRegex));
  let hasLongNumbers = false;
  let hasShortNumbers = false;
  terms.forEach((term) => {
    if (/^\d+$/.test(term)) {
      if (term.length >= config.typesense.infixThreshold) {
        hasLongNumbers = true;
      } else {
        hasShortNumbers = true;
      }
    }
  });
  return hasLongNumbers && !hasShortNumbers;
};

export const fetchResults = (value, itemType) => (dispatch, getState) => {
  const state = getState();
  const index = getIndex(state, config.typesense.indexDocs);
  dispatch(searchRequest(value));
  const { id, type } = getShopOrBrandIdAndType(state);
  const tags = [id];
  if (type === 'shop') {
    const shop = getActiveShop(state);
    if (itemType === 'customer' && shop.brand && shop.brand.showBrandCustomersToShops) {
      tags.push(shop.brand.id);
    }
  }
  let sortBy = '';
  let filters = `tags:=[${tags.join(',')}]`;
  if (itemType) {
    filters += ` && type:=${itemType}`;
  }
  if (itemType === 'appointment') {
    filters += ` && dateTimeUnix:<${moment().add(90, 'days').unix()}`;
    sortBy = 'dateTimeUnix:desc';
  }
  return index
    .documents()
    .search({
      ...config.typesense.options,
      q: value,
      query_by: 'name,phone,email,services,customer,barber',
      filter_by: filters,
      sort_by: sortBy || undefined,
      infix: shouldEnableInfix(value) ? 'fallback' : 'off',
    })
    .then(results => dispatch(searchSuccess(results.hits)))
    .catch(error => dispatch(searchError(error)));
};

export const fetchResultsBarber = (value, itemType) => (dispatch, getState) => {
  const state = getState();
  const client = getClient(state);
  dispatch(searchRequest(value));
  const barber = getBarberUser(state);
  const isRentalShop = getIsRentalShop(state);
  const tags = [barber.id];
  const shop = getActiveShop(state);
  const locationTypeCheck = getFeatureFlags()[flagNames.SHARE_BRAND_CUSTOMERS_TO_RENTAL_BARBERS]
    || !isRentalShop;
  if (itemType === 'customer' && locationTypeCheck && shop.brand?.showBrandCustomersToBarbers) {
    tags.push(shop.brand.id);
  }
  /**
   * Add shop ID to tags so that barber can search products under his shop
   */
  if (itemType === 'product') {
    tags.push(shop.id);
  }
  let filters = `tags:=[${tags.join(',')}]`;
  let sortBy = '';
  if (itemType) {
    filters += ` && type:=${itemType}`;
  }
  if (!barber.permissions.appointments.history) {
    filters += ` && dateTimeUnix:>${moment().unix()}`;
  }
  if (itemType === 'appointment') {
    filters += ` && dateTimeUnix:<${moment().add(90, 'days').unix()}`;
    sortBy = 'dateTimeUnix:desc';
  }
  return client
    .collections(config.typesense.indexDocs)
    .documents()
    .search({
      ...config.typesense.options,
      q: value,
      query_by: 'name,phone,email,services,customer,barber',
      filter_by: filters,
      sort_by: sortBy || undefined,
      infix: shouldEnableInfix(value) ? 'fallback' : 'off',
    })
    .then(results => dispatch(searchSuccess(results.hits)))
    .catch(error => dispatch(searchError(error)));
};

export const fetchBarberCustomerResults = value => (dispatch, getState) => {
  const state = getState();
  const index = getIndex(state, config.typesense.indexDocs);
  dispatch(searchRequest(value));
  const tags = [getBarberUserId(state)];
  const shop = getActiveShop(state);
  const isRentalShop = getIsRentalShop(state);
  const locationTypeCheck = getFeatureFlags()[flagNames.SHARE_BRAND_CUSTOMERS_TO_RENTAL_BARBERS]
    || !isRentalShop;
  if (locationTypeCheck && shop.brand?.showBrandCustomersToBarbers) {
    tags.push(shop.brand.id);
  }
  return index
    .documents()
    .search({
      ...config.typesense.options,
      q: value,
      query_by: 'name,phone,email',
      filter_by: `tags:=[${tags.join(',')}] && type:=customer`,
      infix: shouldEnableInfix(value) ? 'fallback' : 'off',
    })
    .then(results => dispatch(searchSuccess(results.hits)))
    .catch(error => dispatch(searchError(error)));
};

export const fetchShopCustomerResults = value => (dispatch, getState) => {
  const state = getState();
  const index = getIndex(state, config.typesense.indexDocs);
  dispatch(searchRequest(value));
  const shop = getActiveShop(state);
  const tags = [shop.id];
  if (shop.brand && shop.brand.showBrandCustomersToShops) {
    tags.push(shop.brand.id);
  }
  return index
    .documents()
    .search({
      ...config.typesense.options,
      q: value,
      query_by: 'name,phone,email',
      filter_by: `tags:=[${tags.join(',')}] && type:=customer`,
      infix: shouldEnableInfix(value) ? 'fallback' : 'off',
    })
    .then(results => dispatch(searchSuccess(results.hits)))
    .catch(error => dispatch(searchError(error)));
};

export const fetchBrandsAdmin = value => (dispatch, getState) => {
  const state = getState();
  const index = getIndex(state, config.typesense.indexBrands);
  dispatch(searchRequest(value));
  return index
    .documents()
    .search({
      ...config.typesense.options,
      q: value,
      query_by: 'name',
    })
    .then(results => dispatch(searchSuccess(results.hits)))
    .catch(error => dispatch(searchError(error)));
};

export const fetchShopResultsAdmin = value => (dispatch, getState) => {
  const state = getState();
  const index = getIndex(state, config.typesense.indexShops);
  dispatch(searchRequest(value));
  return index
    .documents()
    .search({
      ...config.typesense.options,
      q: value,
      query_by: 'name,address.city,phone,email',
      infix: shouldEnableInfix(value) ? 'fallback' : 'off',
    })
    .then(results => dispatch(searchSuccess(results.hits)))
    .catch(error => dispatch(searchError(error)));
};

export const fetchUserResultsAdmin = value => (dispatch, getState) => {
  const state = getState();
  const index = getIndex(state, config.typesense.indexUsers);
  dispatch(searchRequest(value));
  return index
    .documents()
    .search({
      ...config.typesense.options,
      q: value,
      query_by: 'username,shopNames,userType',
      filter_by: 'userType:=[barber,shop]',
    })
    .then(results => dispatch(searchSuccess(results.hits)))
    .catch(error => dispatch(searchError(error)));
};

export const clearResults = () => ({
  type: types.SEARCH_CLEAR,
});

export const fetchCustomerResultsAdmin = value => (dispatch, getState) => {
  const state = getState();
  const index = getIndex(state, config.typesense.indexUsers);
  dispatch(searchRequest(value));
  return index
    .documents()
    .search({
      ...config.typesense.options,
      q: value,
      query_by: 'username,customer.name,customer.phone,customer.email',
      filter_by: 'userType:=customer',
      infix: shouldEnableInfix(value) ? 'fallback' : 'off',
    })
    .then(results => dispatch(searchSuccess(
      map(results.hits, hit => ({ ...hit, document: { ...hit.document, type: 'adminCustomer' } }))
    )))
    .catch(error => dispatch(searchError(error)));
};

export const fetchBarberResultsAdmin = value => (dispatch, getState) => {
  const state = getState();
  const index = getIndex(state, config.typesense.indexDocs);
  dispatch(searchRequest(value));
  return index
    .documents()
    .search({
      ...config.typesense.options,
      q: value,
      query_by: 'name',
      filter_by: 'type:=barber',
    })
    .then(results => dispatch(searchSuccess(
      map(results.hits, hit => ({ ...hit, document: { ...hit.document, type: 'adminBarber' } }))
    )))
    .catch(error => dispatch(searchError(error)));
};
